import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SEOHead from "../components/shared/SEOHead.jsx";
import JsonLd from "../components/shared/JsonLd.jsx";
import { buildBreadcrumbJsonLd } from "../utils/jsonld.js";
import TurnstileWidget from "../components/shared/TurnstileWidget.jsx";
import { errorAria } from "../utils/formA11y.js";
import { API_BASE_URL } from "../utils/api.js";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const DATENSCHUTZ_PATH = { de: "datenschutz", en: "privacy" };

const kontaktSchema = z.object({
  subject: z.string().min(1),
  name: z.string().min(1),
  email: z.string().email(),
  message: z.string().min(1),
  gdprConsent: z.boolean().refine((v) => v === true),
  honeypot: z.string().max(0), // must stay empty — bot trap, see §10.5
});

const fieldStyle = {
  fontFamily: "Inter, sans-serif",
  fontSize: "15px",
  color: "var(--color-ink)",
  background: "var(--color-cream)",
  border: "1px solid rgba(10,9,7,0.2)",
  borderRadius: "8px",
  padding: "12px 14px",
  width: "100%",
};

const labelStyle = { display: "flex", flexDirection: "column", gap: "6px", fontSize: "13px", color: "rgba(10,9,7,0.65)" };

const errorStyle = { fontSize: "12px", color: "#b3261e" };

export default function Kontakt({ lang }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("idle");
  const [turnstileToken, setTurnstileToken] = useState("");
  const path = lang === "de" ? "/kontakt" : "/contact";
  const altPath = lang === "de" ? "/contact" : "/kontakt";
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(kontaktSchema) });

  const handleVerify = useCallback((token) => setTurnstileToken(token), []);
  const handleExpire = useCallback(() => setTurnstileToken(""), []);

  const turnstilePending = Boolean(TURNSTILE_SITE_KEY) && !turnstileToken;

  async function onSubmit(values) {
    setStatus("sending");
    try {
      const { honeypot, ...rest } = values;
      const body = { ...rest, turnstileToken };
      const res = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setStatus(res.ok ? "sent" : "error");
    } catch {
      setStatus("error");
    }
  }

  return (
    <>
      <SEOHead lang={lang} path={path} altPath={altPath} title={t("nav.kontakt")} />
      <JsonLd
        data={buildBreadcrumbJsonLd(
          [
            { name: t("nav.home"), path: "/" },
            { name: t("nav.kontakt"), path },
          ],
          lang
        )}
      />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "48px", padding: "64px 40px 96px", maxWidth: "760px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <p className="tl-eyebrow" style={{ margin: 0 }}>
              {t("kontakt.eyebrow")}
            </p>
            <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "44px", lineHeight: 1.1, color: "var(--color-cream)", margin: 0 }}>
              {t("kontakt.title")}
            </h1>
          </div>

          <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "40px" }}>
            <span style={{ fontFamily: "var(--font-serif)", fontSize: "22px", color: "var(--color-ink)" }}>
              {t("kontakt.formTitle")}
            </span>

            <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <input type="text" tabIndex={-1} autoComplete="off" {...register("honeypot")} style={{ display: "none" }} />

              <label style={labelStyle}>
                Betreff
                <input type="text" style={fieldStyle} {...errorAria(errors, "subject")} {...register("subject")} />
                {errors.subject && (
                  <span id="subject-error" role="alert" style={errorStyle}>
                    Pflichtfeld
                  </span>
                )}
              </label>

              <label style={labelStyle}>
                Name
                <input type="text" style={fieldStyle} {...errorAria(errors, "name")} {...register("name")} />
                {errors.name && (
                  <span id="name-error" role="alert" style={errorStyle}>
                    Pflichtfeld
                  </span>
                )}
              </label>

              <label style={labelStyle}>
                E-Mail
                <input type="email" style={fieldStyle} {...errorAria(errors, "email")} {...register("email")} />
                {errors.email && (
                  <span id="email-error" role="alert" style={errorStyle}>
                    Bitte gültige E-Mail angeben
                  </span>
                )}
              </label>

              <label style={labelStyle}>
                Nachricht
                <textarea
                  style={{ ...fieldStyle, minHeight: "120px", resize: "vertical" }}
                  {...errorAria(errors, "message")}
                  {...register("message")}
                />
                {errors.message && (
                  <span id="message-error" role="alert" style={errorStyle}>
                    Pflichtfeld
                  </span>
                )}
              </label>

              <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", fontSize: "12px", color: "rgba(10,9,7,0.6)" }}>
                <input
                  type="checkbox"
                  style={{ marginTop: "2px", accentColor: "var(--color-gold)" }}
                  {...errorAria(errors, "gdprConsent")}
                  {...register("gdprConsent")}
                />
                <span>
                  {t("kontakt.gdprConsentPrefix")}{" "}
                  <Link to={`/${lang}/${DATENSCHUTZ_PATH[lang]}`} target="_blank" rel="noopener" style={{ color: "var(--color-ink)", textDecoration: "underline" }}>
                    {t("kontakt.gdprConsentLinkText")}
                  </Link>
                  {t("kontakt.gdprConsentSuffix")}
                </span>
              </label>
              {errors.gdprConsent && (
                <span id="gdprConsent-error" role="alert" style={errorStyle}>
                  Bitte bestätigen
                </span>
              )}

              {TURNSTILE_SITE_KEY && (
                <TurnstileWidget siteKey={TURNSTILE_SITE_KEY} onVerify={handleVerify} onExpire={handleExpire} />
              )}

              <button
                type="submit"
                className="tl-pill tl-pill-dark"
                style={{ padding: "14px 28px", alignSelf: "flex-start", opacity: status === "sending" ? 0.6 : 1 }}
                disabled={status === "sending" || turnstilePending}
              >
                {t("kontakt.sendCta")}
              </button>

              {status === "sent" && (
                <p role="status" style={{ fontSize: "14px", color: "var(--color-ink)" }}>
                  {t("kontakt.sentMessage")}
                </p>
              )}
              {status === "error" && (
                <p role="alert" style={errorStyle}>
                  {t("kontakt.errorMessage")}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
